import axios from "axios";
import { CLIRNET_API } from "./endpoints.js";
import { getStoredToken, refreshToken, msUntilExpiry, pendingRefresh } from "./auth.js";

// Refresh before a request whose token has less than this left.
const REQUEST_EXPIRY_BUFFER_MS = 60_000;

function getConfigBearer(config) {
  const header = config?.headers?.get
    ? config.headers.get("Authorization") || config.headers.get("authorization")
    : config?.headers?.Authorization || config?.headers?.authorization;
  return typeof header === "string" ? header.replace("Bearer ", "").trim() : null;
}

export const apiClient = axios.create({
  // No timeout by default means a stalled request hangs forever and freezes whatever
  // awaited it (spinners, "Fetching results…", disabled switches). 15s is generous for
  // this API and turns a hang into a normal error path.
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

apiClient.interceptors.request.use(async (config) => {
  config.baseURL = CLIRNET_API.BASE_URL;
  config.headers = config.headers || {};
  if (!config.headers.Authorization) {
    let token = getStoredToken();
    if (token && !config.skipAuthRefresh) {
      const left = msUntilExpiry(token);
      const inFlight = pendingRefresh();
      if (left !== null && left <= REQUEST_EXPIRY_BUFFER_MS) {
        try {
          token = await refreshToken();
        } catch (e) {
          // Refresh already failed here — the request still goes out with the
          // old token (it may have a few seconds of real life left inside the
          // buffer), but the response interceptor must not repeat the same
          // doomed refresh if this now 401s. Mirrors the flag it checks itself.
          config._retriedAfterRefresh = true;
          console.warn("[auth] proactive refresh failed:", e.message);
        }
      } else if (inFlight) {
        // A refresh is retiring this token right now: wait for its replacement rather
        // than send one the API is about to refuse.
        try {
          token = await inFlight;
        } catch {
          // That refresh failed: send the stored token as it is, but if this request
          // now 401s, don't start the same doomed refresh a second time.
          config._retriedAfterRefresh = true;
        }
      }
    }
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  // No clearing of the "refused" flag here: it belongs to the stored token, and a refused
  // token never works again - a success arriving after the refusal was answered before it
  // (a slow request), and clearing on it flickered the "expired" screen back to the page.
  (res) => res,
  async (error) => {
    const config = error.config;
    if (
      error.response?.status === 401 &&
      config &&
      !config.skipAuthRefresh &&
      !config._retriedAfterRefresh
    ) {
      config._retriedAfterRefresh = true;
      // Sent with a token that has been replaced since (a refresh finished while this was
      // in flight): the API refused the old token, not the session. Retry with the current
      // one - refreshing again would retire the token other requests are retrying with,
      // and those retries (one each) would then fail.
      const sent = getConfigBearer(config);
      const current = getStoredToken();
      if (sent && current && sent !== current) {
        // If the current token is itself being refreshed, retry with its replacement.
        const inFlight = pendingRefresh();
        const token = inFlight ? await inFlight.catch(() => getStoredToken()) : current;
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
        return apiClient(config);
      }
      try {
        const fresh = await refreshToken();
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${fresh}`,
        };
        return apiClient(config);
      } catch (e) {
        console.warn("[auth] refresh failed:", e.message);
      }
    }
    const msg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Request failed";
    const err = new Error(msg);
    err.status = error.response?.status;
    return Promise.reject(err);
  },
);
